<script>
    import Icon from '$component/Icon.svelte';
    // @ts-nocheck
    import {t} from '$utils/lang.js';
    import {scrollToFirstError} from '$utils/scrollToError.js';
    import {store as userStore} from '$lib/userStore.svelte.js';
    import {
        paymentStore,
        fetchShippingAndPayment,
        selectShipping,
        selectPayment,
        selectLeasingType,
        saveLeasingData,
        setLeasingValid,
        handleGatewayApproved,
    } from '$lib/paymentStore.svelte.js';
    import {refreshShipping, refreshBasket, store as basketStore} from '$lib/basketStore.svelte.js';
    import PaypalGateway from '$component/Payments/PaypalGateway.svelte';
    import PaypalAcdc from '$component/Payments/PaypalAcdc.svelte';
    import {buttonStyles} from '$m7-library/main';
    import ShippingMethodCard from '$component/ShippingMethodCard.svelte';
    import PaymentMethodCard from '$component/PaymentMethodCard.svelte';
    import PhoneSection from '$component/PhoneSection.svelte';
    import LeasingWidget from '$component/LeasingWidget.svelte';
    import DeliveryDateSection from '$component/DeliveryDateSection.svelte';
    import MarketSwitchNotice from '$component/MarketSwitchNotice.svelte';

    import { devLog as _devLog } from '$utils/devLog.js';
    const devLog = (tag, ...args) => _devLog(`Payment:${tag}`, ...args);

    const {
        status = 'active',
        isExpressMode = false,
        onContinue = () => {},
        onActivate = () => {},
        onBack = () => {},
    } = $props();

    let phoneState = $state({requiresPhone: false, phone: '', phoneOk: true});

    // "Stay here" hides the notice for this browser session; the basket stays as it is, no redirect.
    const noticeKey = $derived(`m7_market_notice_dismissed_${basketStore.countryMarket?.code ?? ''}`);
    let noticeDismissed = $state(false);
    $effect(() => {
        try { noticeDismissed = sessionStorage.getItem(noticeKey) === '1'; } catch { noticeDismissed = false; }
    });
    const dismissNotice = () => {
        noticeDismissed = true;
        try { sessionStorage.setItem(noticeKey, '1'); } catch { /* storage may be blocked */ }
    };

    const isPackstation = $derived(userStore.user?.shippingAddress?.street === 'Packstation');
    const shippingMethods = $derived(paymentStore.shippingMethods ?? []);
    const paymentMethods = $derived((paymentStore.paymentMethods ?? []).filter(p => p.type !== 'EXPRESS' && !p.metadata?.hide));
    const selectedShippingId = $derived(paymentStore.selectedShippingId);
    const selectedPaymentId = $derived(paymentStore.selectedPaymentId);

    const selectedShipping = $derived(
        shippingMethods.find(s => s.shippingSet?.id === selectedShippingId)
    );
    const selectedPayment = $derived(
        paymentMethods.find(p => p.id === selectedPaymentId)
            ?? (paymentStore.paymentMethods ?? []).find(p => p.id === selectedPaymentId)
    );

    const requiresPhone = $derived(phoneState.requiresPhone);
    const phoneOk = $derived(phoneState.phoneOk);

    const isLeasing = $derived(selectedPayment?.type === 'LEASING');
    const isPaypalGateway = $derived(selectedPayment?.metadata?.sdk === 'paypal-gateway');
    const isPaypalAcdc = $derived(selectedPayment?.metadata?.sdk === 'paypal-acdc');
    const isPaypalPui = $derived(selectedPayment?.metadata?.sdk === 'paypal-pui');
    const isEasyCredit = $derived(selectedPayment?.metadata?.sdk === 'easycredit');

    // Leasing-Sub-Selection: paymentMethods enthält ys_leasing als 1 Card.
    // Sobald sie selektiert ist, zeigt sich darunter der LeasingType-Picker.
    // Aktive Auswahl kommt aus paymentStore.selectedLeasingId (server-side
    // dynvalue.leasingId).
    const leasingTypes = $derived(paymentStore.leasingTypes ?? []);
    const selectedLeasingId = $derived(paymentStore.selectedLeasingId ?? null);
    const leasingMeta = $derived(leasingTypes.find(lt => lt.id === selectedLeasingId) ?? null);

    const needsGatewayApproval = $derived(false);
    // hasShipping guard: when the delivery country has zero shipping methods,
    // selectedShippingId may still hold a stale id from a previous selection,
    // so the truthy check alone wouldn't block the continue button.
    const hasShipping = $derived(shippingMethods.length > 0);
    // Selected payment may carry metadata.unavailable=true (e.g. EasyCredit when
    // delivery ≠ billing). Don't let the user advance with an unavailable method —
    // the gateway would just bounce them back with a generic toast.
    const selectedPaymentUnavailable = $derived(selectedPayment?.metadata?.unavailable === true);
    const canContinue = $derived(
        hasShipping && (
        isExpressMode
            ? !!selectedShippingId && !paymentStore.saving
            : !!selectedShippingId
              && !!selectedPaymentId
              && !selectedPaymentUnavailable
              && (!isLeasing || !!selectedLeasingId)
              && !paymentStore.saving
              && (!needsGatewayApproval || paymentStore.gatewayApproved)
        )
    );

    let leasingValidating = $state(false);
    let advanceAfterValid = $state(false);
    let stepEl = $state(); // active-step root — anchor for shadow-DOM error scroll

    async function handleLeasingChange({slug, leasingId, isValid, dynvalue}) {
        setLeasingValid(isValid);
        if (isValid) {
            await saveLeasingData(leasingId, slug, dynvalue);
            if (advanceAfterValid) {
                advanceAfterValid = false;
                onContinue();
            }
        }
    }

    function handleLeasingValidation({next}) {
        if (next === 'reset') leasingValidating = false;
    }

    // Only fetch when status becomes 'active'
    $effect(() => {
        if (status === 'active') {
            fetchShippingAndPayment().then(() => {
                if (isPackstation) {
                    const dhl = paymentStore.shippingMethods.find(s => s.shippingSet?.deliveryType === 'dhl');
                    if (dhl && paymentStore.selectedShippingId !== dhl.shippingSet.id) {
                        selectShipping(dhl.shippingSet.id);
                    }
                }
            });
        }
    });

    async function handleSelectShipping(id) {
        if (id === selectedShippingId) return;
        devLog('btn:selectShipping', {id});
        await selectShipping(id);
        refreshShipping();
        // PHP must recalculate basket when shipping set changes VAT country (ys_vat_shipping_country,
        // e.g. Selbstabholung → DE override: CH customer pays 19% German VAT for in-store pickup).
        await fetch('/index.php?cl=m7-checkout-dbc&fnc=recalculateBasket', {credentials: 'include'}).catch(() => {});
        await refreshBasket();
    }

    // reference: [1] — der Kunde hakt die Versicherung im WARENKORB an und waehlt
    // die Zahlungsart erst danach. Nimmt der Server sie wegen Leasing zurueck,
    // passiert das auf einem Schritt, den er gerade nicht sieht — deshalb hier
    // sagen, dass es passiert ist. `active` VOR dem Wechsel merken, weil danach
    // nicht mehr unterscheidbar ist, ob er sie je wollte.
    let versicherungEntfernt = $state(false);

    async function handleSelectPayment(id) {
        if (id === selectedPaymentId) return;
        devLog('btn:selectPayment', {id});
        const versicherungWarAktiv = basketStore.insurance?.active === true;
        await selectPayment(id);
        // Recompute basket: switching to/from a leasing payment adds/removes the
        // leasing-markup line. parseBasketData derives the markup from the
        // session paymentid, so a refresh reflects the change immediately.
        await refreshBasket();
        versicherungEntfernt = versicherungWarAktiv
            && basketStore.insurance?.blockedByLeasing === true;
    }

    async function handleSelectLeasingType(id) {
        if (id === selectedLeasingId) return;
        devLog('btn:selectLeasingType', {id});
        await selectLeasingType(id);
        // Different leasing types carry different markup percentages — refresh
        // the basket so the markup line and total update.
        await refreshBasket();
    }

    async function handleContinue() {
        devLog('btn:continue', {
            canContinue,
            requiresPhone,
            phoneOk,
            phone: phoneState.phone || null,
            selectedShippingId,
            selectedPaymentId,
            isExpressMode,
        });
        if (!canContinue) {
            scrollToFirstError(stepEl);
            return;
        }
        if (requiresPhone && !phoneOk) {
            devLog('continue:blocked:phone');
            // Shadow-DOM aware: target the phone field within the step root.
            scrollToFirstError(stepEl, 'input[name="phone"]');
            return;
        }
        // Save entered phone to server + update userStore so Order step sees it immediately.
        // Capture the promise so EasyCredit redirect can await it before navigating away.
        let phoneSavePromise = Promise.resolve();
        if (phoneState.phone && requiresPhone) {
            devLog('savePhone', {phone: phoneState.phone});
            userStore.user.billingAddress.phone = phoneState.phone;
            phoneSavePromise = fetch(`/index.php?${new URLSearchParams({cl: 'm7-checkout-duc', fnc: 'savePhone'})}`, {
                method: 'POST',
                credentials: 'include',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({phone: phoneState.phone}),
            }).catch(() => {});
        }
        if (isLeasing && !paymentStore.leasingIsValid) {
            leasingValidating = true;
            advanceAfterValid = true;
            return;
        }
        if (isEasyCredit) {
            // If the customer already completed the EC flow and didn't modify
            // anything that would invalidate the Vorgang (basket/address mutations
            // clear the EC session state in the proxy), the preauthorization is
            // still valid → skip the redirect and just advance to the order step.
            // easyCreditReady works for both Ratenkauf and Rechnungskauf; the
            // latter has no interest to key off.
            if (basketStore.easyCreditReady) {
                devLog('continue:easycredit:skip-redirect', {ready: true});
                await phoneSavePromise;
                onContinue();
                return;
            }
            devLog('continue:easycredit:redirect', {paymentId: selectedPayment?.id});
            await phoneSavePromise;
            window.location.href = `/index.php?${new URLSearchParams({cl: 'm7-easycredit', fnc: 'initializeandredirect', payment: selectedPayment?.id ?? ''})}`;
            return;
        }
        devLog('continue:ok');
        onContinue();
    }
</script>

{#if status === 'completed'}
    <!-- Compact completed state -->
    <button
        type="button"
        onclick={onActivate}
        class="w-full bg-white rounded-md border border-grey px-5.5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 text-left"
        data-checkout-step="payment" data-checkout-state="completed" data-checkout-action="edit-payment"
    >
        <div class="flex items-center gap-3">
            <div class="bg-cyprus text-white rounded-full size-7.5 flex items-center justify-center shrink-0">
                <svg class="size-4" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 10l4 4 8-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <span class="font-bold text-base">{t('Versand & Zahlung')}</span>
        </div>
        <Icon name="down" class="w-4 h-4 text-gray-400 shrink-0"/>
    </button>
{:else if status === 'upcoming'}
    <!-- Locked upcoming state -->
    <div class="bg-white rounded-md border border-grey px-5.5 py-4 flex items-center gap-3" data-checkout-step="payment" data-checkout-state="upcoming">
        <div class="bg-gray-100 text-gray-103 rounded-full size-7.5 flex items-center justify-center shrink-0">
            <span class="font-bold text-sm">2</span>
        </div>
        <span class="font-bold text-base text-gray-103">{t('Versand & Zahlung')}</span>
    </div>
{:else}
    <!-- Active state -->
    <div class="flex flex-col gap-4" data-checkout-step="payment" data-shipping-id={selectedShippingId ?? 'none'} data-payment-id={selectedPaymentId ?? 'none'} data-payment-leasing={isLeasing} data-payment-leasing-meta={leasingMeta ? 'yes' : 'no'} data-payment-type={selectedPayment?.type ?? 'null'} bind:this={stepEl}>

        <div class="bg-white rounded-md border border-grey">
            <div class="flex flex-col gap-5 px-5.5 py-5.5">
                {#if paymentStore.loading && shippingMethods.length === 0}
                    <div class="text-gray-103 text-sm py-6 text-center">
                        {t('Versand- und Zahlungsoptionen werden geladen…')}
                    </div>
                {:else}
                    <!-- Versandart -->
                    <section id="shipping" class="flex flex-col gap-3" data-checkout-section="shipping" data-basket-count={shippingMethods.length} data-shipping-packstation={isPackstation}>
                        <h3 class="font-bold text-base">{t('Versandart')}</h3>
                        {#if shippingMethods.length === 0 && basketStore.countryMarket && !noticeDismissed}
                            <!-- The delivery country belongs to another market: offer the switch (task 185). -->
                            <MarketSwitchNotice countryMarket={basketStore.countryMarket} ondismiss={dismissNotice} />
                        {:else if shippingMethods.length === 0}
                            <!-- No shipping method available for the chosen delivery country
                                 (e.g. single battery to PL/CZ/BE/NL/AT). Legacy message. -->
                            <div class="p-4 rounded-lg bg-scarlet/10 border border-scarlet text-scarlet text-sm" data-checkout-section="no-shipping">
                                {t('Leider können wir dieses Produkt nicht in das ausgewählte Lieferland verschicken.')}
                            </div>
                        {:else if isPackstation}
                            <!-- Packstation: DHL fixed, no picker -->
                            <div class="flex flex-col gap-3">
                                {#each shippingMethods.filter(m => m.shippingSet?.deliveryType === 'dhl') as method (method.shippingSet.id)}
                                    <ShippingMethodCard {method} selected={true} onselect={() => {}} />
                                {/each}
                            </div>
                        {:else}
                            <div class="flex flex-col gap-3">
                                {#each shippingMethods as method (method.shippingSet.id)}
                                    <ShippingMethodCard
                                        {method}
                                        selected={selectedShippingId === method.shippingSet.id}
                                        onselect={handleSelectShipping}
                                    />
                                {/each}
                            </div>
                        {/if}
                    </section>

                    {#if hasShipping}
                    <!-- Phone block (only when shipping needs a phone) -->
                    <PhoneSection onchange={(s) => phoneState = s}/>

                    {#if isExpressMode}
                    <!-- Express Mode: payment locked to PayPal Express -->
                    <section id="payment-methods" class="flex flex-col gap-3" data-checkout-section="payment-methods-express">
                        <h3 class="font-bold text-base">{t('Zahlungsart')}</h3>
                        <div class="p-4 rounded-lg border-2 border-green bg-green/5 flex items-center gap-3" data-payment-option="express-locked">
                            <span class="shrink-0 size-5 rounded-full border-2 border-green flex items-center justify-center">
                                <span class="size-2.5 rounded-full bg-green"></span>
                            </span>
                            <span class="font-bold text-xsm text-black">{selectedPayment?.title ?? 'Express'}</span>
                        </div>
                        <button
                            type="button"
                            onclick={() => selectPayment('')}
                            class="text-sm text-elm underline cursor-pointer text-left"
                            data-checkout-action="exit-express"
                        >{t('Zahlungsmethode ändern')}</button>
                    </section>
                    {:else}
                    <!-- Zahlungsart -->
                    <section id="payment-methods" class="flex flex-col gap-3" data-checkout-section="payment-methods" data-basket-count={paymentMethods.length}>
                        <h3 class="font-bold text-base">{t('Zahlungsart')}</h3>
                        <div class="flex flex-col gap-3">
                            {#each paymentMethods as method (method.id)}
                                <PaymentMethodCard
                                    {method}
                                    selected={selectedPaymentId === method.id}
                                    onselect={handleSelectPayment}
                                />
                            {/each}
                        </div>
                    </section>

                    {#if versicherungEntfernt}
                        <!-- reference: [1] -->
                        <div class="p-3 rounded-lg border border-scarlet bg-scarlet/5 text-xsm text-cyprus"
                             data-checkout-section="insurance-removed" role="status">
                            {t('Die kostenlose 30-Tage-Versicherung wurde entfernt: sie ist mit Fahrradleasing nicht kombinierbar. Wähle eine andere Zahlungsart, wenn du sie behalten möchtest.')}
                        </div>
                    {/if}

                    {#if isLeasing && leasingTypes.length > 0}
                        <!-- Leasing-Sub-Selection: Anbieter-Picker. Erscheint nur
                             wenn die Leasing-Payment-Card selektiert ist. Bis ein
                             Anbieter gewählt ist, blockt canContinue die Weiter-
                             Schaltung. -->
                        <section class="flex flex-col gap-3" data-checkout-section="leasing-picker">
                            <h3 class="font-bold text-base">🚲 {t('Wähle deinen Leasinganbieter')}</h3>
                            <div class="grid grid-cols-2 gap-3">
                                {#each leasingTypes as lt (lt.id)}
                                    <button
                                        class="min-h-[80px] p-4 rounded-lg border-2 transition-colors flex items-center justify-center text-center text-xsm font-bold cursor-pointer {selectedLeasingId === lt.id ? 'border-green bg-green/5' : 'border-gray-102 bg-white hover:border-gray-103'}"
                                        onclick={() => handleSelectLeasingType(lt.id)}
                                        type="button"
                                        data-leasing-option={lt.id}
                                        data-select-nameed={selectedLeasingId === lt.id}
                                    >
                                        {lt.name}
                                    </button>
                                {/each}
                            </div>
                        </section>

                        {#if selectedLeasingId && leasingMeta}
                            <LeasingWidget
                                leasingId={leasingMeta.id}
                                slug={leasingMeta.slug}
                                onchange={handleLeasingChange}
                                validate={leasingValidating}
                                onvalidation={handleLeasingValidation}
                                initialValues={selectedPayment?.metadata?.dynvalue ?? null}
                            />
                        {/if}
                    {/if}

                    {/if}

                    {#if isEasyCredit && selectedPayment?.metadata?.agreementText}
                        <div class="text-xsm text-gray-103" data-checkout-section="easycredit-consent">
                            {@html selectedPayment.metadata.agreementText}
                        </div>
                    {/if}

                    {#if isEasyCredit && selectedPayment?.metadata?.variant === 'bill' && selectedPayment?.metadata?.billInfoText}
                        <div class="text-xsm text-gray-103" data-checkout-section="easycredit-bill-info">
                            {selectedPayment.metadata.billInfoText}
                        </div>
                    {/if}

                    <!-- Wunsch-Zustelldatum (nur wenn Versandart + Zahlungsart beide
                         ys_use_shipping_date=1). Komponente blendet sich selbst aus. -->
                    <DeliveryDateSection/>

                    {#if paymentStore.error}
                        <!-- data-checkout-section: macht den Hinweis für Tests
                             greifbar (analog order-error in Order.svelte). -->
                        <div class="text-scarlet text-sm" data-checkout-section="payment-error">{paymentStore.error}</div>
                    {/if}

                    <div class="flex gap-3">
                        <button
                            class={buttonStyles['elm-button']}
                            disabled={!canContinue}
                            class:opacity-50={!canContinue}
                            class:cursor-not-allowed={!canContinue}
                            onclick={handleContinue}
                            type="button"
                            data-checkout-action="continue" aria-disabled={!canContinue}
                        >
                            {t('Weiter zur Überprüfung')}
                        </button>
                    </div>
                    {/if}
                {/if}
            </div>
        </div>

    </div>
{/if}
